import { useCallback, useEffect, useMemo, useState } from 'react';
import { submitAutoCreateGapResolution } from '../services/api';

/**
 * What the operator decided about each paused step, and the one way it is sent.
 *
 * <h3>Why this is a hook and not a second screen</h3>
 *
 * `AutoCreateWizard.jsx:467-563` already owned this: which steps are waiting,
 * what a resolution looks like, when they are all answered, and the shape that
 * goes to the backend. The build trail needs the same answer from a different
 * surface. Computing it twice is how a payload contract comes to disagree with
 * itself, and the copy that disagrees quietly is the one that writes a binding
 * nobody chose.
 *
 * What stays behind is the rendering. `GapRow` draws from the v2 palette held at
 * `AutoCreateWizard.jsx:38`; the wizard surface is drawn in p7
 * (`SmartAgentWizardPage.jsx:31-38`). Sharing the component would carry one
 * surface's tokens onto the other. So: one state machine, one submission, two
 * paint jobs.
 *
 * <h3>Not every gap is a question</h3>
 *
 * A {@code confirm_bind} arrives already matched. Its auto-bound candidate is
 * seeded as the answer, so the operator only touches what they actually want to
 * change. A hard gap carries no answer and waits for a person. Stopping on both
 * would ask people to re-decide what the matcher was confident about, and a
 * surface that stops for no reason teaches people to click through it.
 *
 * <h3>The three actions are the backend's words</h3>
 *
 * {@code use_match} with a registryId, {@code mint_derived}, {@code skip_step} —
 * recorded by the drafter as BIND_EXISTING, MINT_DERIVED and SKIPPED. They are
 * not renamed on the way through, because a second vocabulary for the same three
 * outcomes is a translation layer that eventually mistranslates.
 *
 * @param {Object} gaps  `live.pausedGap` — payloads keyed by stepId
 * @param {string} runId the run to resume
 */
export function useGapResolution({ gaps = {}, runId }) {
  const [resolutions, setResolutions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const stepIds = useMemo(() => Object.keys(gaps || {}), [gaps]);
  const stepKey = stepIds.join(',');

  /* Seed the confirms, never the hard gaps. Runs on the set of steps rather than
     the gaps object so a re-render with an equal-but-new object does not re-seed
     over a decision the operator has already made. */
  useEffect(() => {
    setResolutions((prev) => {
      const next = { ...prev };
      let changed = false;
      stepIds.forEach((id) => {
        if (next[id]) return;
        const g = gaps[id];
        if (g && g.reviewKind === 'confirm_bind') {
          const cur = (g.candidates || []).find((c) => c.autoBound) || (g.candidates || [])[0];
          if (cur) {
            next[id] = {
              action: 'use_match',
              registryId: cur.registryId,
              displayName: cur.displayName,
              kind: cur.kind,
              source: 'auto_confirmed',
            };
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
    // NOTE: keyed on `stepKey`, not on `gaps` — a re-render handing back an
    // equal-but-new gaps object must not re-run the seed over a decision the
    // operator has already made. No disable directive here: the project's ESLint
    // config does not register react-hooks/exhaustive-deps, and naming the rule
    // is itself a build-breaking error (RC-65 trap; see Modal.jsx:103-108 and
    // AgentMarketplacePageV2.jsx:210-212).
  }, [stepKey]);

  /**
   * Record a decision for one step, exactly as given.
   *
   * The hook does not invent {@code source}. That field is a provenance claim —
   * it says whether a human chose this binding or the platform confirmed a match
   * on their behalf — and a default would let a caller that forgot to say so be
   * recorded as a human decision it never made. The seed above sets
   * {@code auto_confirmed} because the hook is the thing doing the confirming and
   * is entitled to label its own act; a caller's choice has to declare itself.
   * Every existing call site already does (`AutoCreateWizard.jsx:623, :671, :703,
   * :713`).
   */
  const choose = useCallback((stepId, resolution) => {
    setResolutions((prev) => ({ ...prev, [stepId]: { ...resolution } }));
  }, []);

  const allResolved = stepIds.length > 0 && stepIds.every((id) => resolutions[id]?.action);

  /** The steps still holding the trail up. A seeded confirm is not one of them. */
  const awaitingPerson = stepIds.filter((id) => !resolutions[id]?.action);

  /**
   * Send every step's decision at once.
   *
   * Refuses while anything is unanswered rather than sending a partial set: the
   * backend resumes the pipeline on this call, and resuming with a step nobody
   * decided is how a binding gets chosen by default.
   */
  const submit = useCallback(async () => {
    if (!stepIds.length || !stepIds.every((id) => resolutions[id]?.action)) return false;
    setError(null);
    setSubmitting(true);
    try {
      await submitAutoCreateGapResolution(
        runId,
        stepIds.map((stepId) => ({ stepId, ...resolutions[stepId] })),
      );
      /* Deliberately still submitting. The run resumes on the backend and the
         stream re-renders the surface; a control that re-enabled here would let
         the same decisions be sent twice. `submitted` is what the caller shows
         instead of a spinner, so a stream that never arrives reads as "resuming",
         not as a button that stopped working. */
      setSubmitted(true);
      return true;
    } catch (e) {
      setError(e?.message || 'Could not submit your decisions.');
      setSubmitting(false);
      return false;
    }
  }, [runId, resolutions, stepIds]);

  return { resolutions, allResolved, awaitingPerson, choose, submit, submitting, submitted, error };
}

export default useGapResolution;
